import React, { useEffect, useRef } from 'react';

/**
 * DeleteConfirmModal - Modal for confirming activity deletion
 */
export const DeleteConfirmModal = ({
  isOpen,
  activityName,
  onConfirm,
  onCancel,
  isDeleting,
  t
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen && !isDeleting) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isDeleting, onCancel]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current && !isDeleting) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      className="modal fade show d-block"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={handleBackdropClick}
      tabIndex="-1"
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t('curriculum.delete_modal.title')}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onCancel}
              disabled={isDeleting}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <p>{t('curriculum.delete_modal.message', { name: activityName })}</p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isDeleting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={onConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </span>
                  {t('curriculum.delete_modal.deleting')}
                </>
              ) : (
                t('common.delete')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;
